<script setup lang="ts">
import BaseIcon from '@/components/ui/BaseIcon.vue';
import type { IconName } from '@/libs/icons';

withDefaults(
  defineProps<{
    active?: boolean;
    /** Notification/line color dot displayed before the label */
    dotColor?: string;
    /** Lucide icon name */
    icon?: IconName;
    label: string;
    removable?: boolean;
  }>(),
  {
    active: false,
    dotColor: undefined,
    icon: undefined,
    removable: false,
  },
);

const emit = defineEmits(['remove']);
</script>

<template>
  <button class="filter-chip" :class="{ 'filter-chip--active': active }" type="button">
    <BaseIcon v-if="icon" :name="icon" class="filter-chip__icon" />
    <span v-if="dotColor" class="filter-chip__dot" :style="{ 'background-color': dotColor }" />
    <span class="filter-chip__label">{{ label }}</span>
    <BaseIcon v-if="removable" name="x" class="filter-chip__icon" @click.stop="emit('remove')" />
  </button>
</template>

<style lang="scss">
.filter-chip {
  align-items: center;
  background-color: $color-white;
  border: 1px solid $color-border-medium;
  border-radius: $radius-pill;
  box-shadow: $shadow-soft;
  cursor: pointer;
  display: inline-flex;
  flex-shrink: 0;
  gap: 6px;
  justify-content: center;
  padding: $spacing-2 $spacing-3;

  &:active {
    background-color: $color-neutral-lower;
  }

  &--active {
    border-color: $color-neutral-highest;
  }

  &__dot {
    border-radius: 50%;
    display: inline-block;
    height: 8px;
    width: 8px;
  }

  &__icon {
    font-size: 16px;
  }

  &__label {
    color: $color-neutral-highest;
    font: $label-small;
    letter-spacing: $label-small-spacing;
    white-space: nowrap;
  }
}
</style>
